import { Component, Element, h, Prop, Watch, State } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  HEADLINE_SCHEME_MAP,
  HEADLINE_SCHEME_CONFIG,
  HEADLINE_SIZE_COPY,
  HEADLINE_SIZE_H1,
  HEADLINE_SIZE_H2,
  HEADLINE_SIZE_H3,
  HEADLINE_SIZE_H4,
  HEADLINE_SIZE_H5
} from "./constants/headline-constants";
import { HeadlineSize, HeadlineSchemeName, HeadlineSchemeDefinition, HeadlineTag } from "./constants/headline-types";
import { HeadlineSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import {
  HeadlineAlignment,
  HEADLINE_H1_ALIGNMENT,
  HEADLINE_H2_ALIGNMENT,
  HEADLINE_H3_ALIGNMENT,
  HEADLINE_H4_ALIGNMENT,
  HEADLINE_H5_ALIGNMENT
} from "../../constants/global-constants";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 */
@Component({
  tag: "eon-ui-headline",
  styleUrl: "headline.scss",
  assetsDirs: ["assets-headline"],
  shadow: true
})
export class Headline implements ColorableComponent<HeadlineSchemeConfiguration<HeadlineSchemeDefinition>> {
  schemeConfig: HeadlineSchemeConfiguration<HeadlineSchemeDefinition> = HEADLINE_SCHEME_CONFIG;

  schemeMap = HEADLINE_SCHEME_MAP;

  @State() activeScheme: HeadlineSchemeDefinition;
  @Element() hostElement: HTMLEonUiHeadlineElement;
  @State() processedText: string;
  @State() seoHeadlineTag: string;
  @State() infoIconComponent: HTMLEonUiInfoIconElement;
  @State() allowHeadlineAlignment: boolean = true;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: HeadlineSchemeName;

  /**
   * The size of the headline.<br>If the property "headlineHtmlTag" is not defined,
   *   this property is used to generate the HTML-Tag for the headline,<br>
   *     e.g.if size="h1" the HTML-Tag "H1" is used for the headline.<br>
   *     **IMPORTANT**: This property **has an** effect on the styles!
   * @example <eon-ui-headline size="h1" text="Better Tomorrow"></eon-ui-headline>
   */
  @Prop() size: HeadlineSize = HEADLINE_SIZE_H3;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline,<br>
   *     e.g. if headline-html-tag="h1" the HTML-Tag "H1" is used for the headline.<br>
   *     **IMPORTANT**: This property **does not** affect the styles!
   * @example <eon-ui-headline size="h5" headline-html-tag="h1" text="Better Tomorrow"></eon-ui-headline>
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Alignment of the headline. Only works when size is set to "h3" as specified in Frontify.
   * @example <eon-ui-headline size="3" alignment="left" text="Better Tomorrow"></eon-ui-headline>
   */
  @Prop() alignment: HeadlineAlignment;

  /**
   * Text content of the headline
   *
   * @example <eon-ui-headline size="h1" text="Better Tomorrow"></eon-ui-headline>
   */
  @Prop() text: string;

  /**
   * A boolean property that can be used to disable line breaks.
   */
  @Prop() disableLineWrap: boolean = false;

  /**
   * If the headline component has its natural margin defined by the e.on brand design. Omit only if the headline is used in spaces where the margin can be removed because of other css
   * (for example when using a headline in a flexbox layout)
   */
  @Prop() margin = true;

  /**
   * Whether or not the headline text should have a text shadow. Use this if the headline is placed on top of an image and you need more contrast between image and headline.
   */
  @Prop() textShadow = false;

  /**
   * A boolean property that can be used to enable line hyphenation.<br>
   *   Important!: If you have activated hyphenation, note that you must then also set the language, see also the property "language".
   */
  @Prop() hyphenation: boolean = false;

  /**
   * if you enabled hyphenation, it is important that the language for the headline is defined.<br>
   *  There is an automatism that finds out in which language the HTML document exists. If no lang attribute is specified at the parent tags, then the language must be set manually.
   *  <br>Look <a href="https://www.w3schools.com/tags/ref_language_codes.asp" target="_blank">here</a> for a List of available language code.
   */
  @Prop() language: string;

  componentWillLoad() {
    this.applyScheme();
    this.updateProcessedText();
    this.handleHeadlineTag();
    this.headlineAlignment();

    this.infoIconComponent = this.hostElement.querySelector('[slot="info-icon"]');
  }

  componentDidLoad() {
    this.setCssVariables();
    this.applyInfoIconScheme();
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("infoIconComponent")
  @Watch("scheme")
  applyInfoIconScheme(): void {
    this.infoIconComponent &&
      this.infoIconComponent.setAttribute("scheme", this.activeScheme.color.replace("eon-", ""));
  }

  /**
   * Executes whenever the text property changes. Replaced the specified linewrap symbol with <br />.
   */
  @Watch("text")
  updateProcessedText() {
    const slashN = "\\n";
    if (this.text && !this.disableLineWrap) {
      this.processedText = this.text.split(slashN).join("<br />");
    } else {
      this.processedText = this.text;
    }
  }

  @Watch("headlineHtmlTag")
  @Watch("size")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(
      this.headlineHtmlTag,
      this.size === "copy" ? "span" : this.size
    );
  }

  @Watch("alignment")
  @Watch("size")
  headlineAlignment() {
    switch (this.size) {
      case HEADLINE_SIZE_H1:
        this.allowHeadlineAlignment = (HEADLINE_H1_ALIGNMENT as ReadonlyArray<string>).includes(this.alignment);
        break;
      case HEADLINE_SIZE_H2:
        this.allowHeadlineAlignment = (HEADLINE_H2_ALIGNMENT as ReadonlyArray<string>).includes(this.alignment);
        break;
      case HEADLINE_SIZE_H3:
        this.allowHeadlineAlignment = (HEADLINE_H3_ALIGNMENT as ReadonlyArray<string>).includes(this.alignment);
        break;
      case HEADLINE_SIZE_H4:
        this.allowHeadlineAlignment = (HEADLINE_H4_ALIGNMENT as ReadonlyArray<string>).includes(this.alignment);
        break;
      case HEADLINE_SIZE_H5:
        this.allowHeadlineAlignment = (HEADLINE_H5_ALIGNMENT as ReadonlyArray<string>).includes(this.alignment);
        break;
      default:
        this.allowHeadlineAlignment = true;
        break;
    }
  }

  render() {
    //build headline tag
    const TagForHeadline = `${this.seoHeadlineTag.toLowerCase()}` as keyof JSX.IntrinsicElements;
    // rename HeadlineTag in TagForHeadline because of using HeadlineTag in "./constants/headline-types" (sonar issue)
    return (
      <TagForHeadline
        lang={this.language}
        class={pfx({
          ["headline"]: true,
          ["info-icon-display"]: this.infoIconComponent,
          [this.alignment]: this.allowHeadlineAlignment,
          ["heading-" + this.size]: true,
          ["no-margin"]: !this.margin,
          ["text-shadow"]: this.textShadow,
          ["hyphenation"]: this.hyphenation,
          ["copy"]: this.size === HEADLINE_SIZE_COPY
        })}
      >
        <span innerHTML={this.processedText}></span>
        {this.infoIconComponent && (
          <div class={pfx("headline-info-icon")}>
            <slot name="info-icon" />
          </div>
        )}
      </TagForHeadline>
    );
  }
}
